import { Box, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

/** 티켓 획득 구간 데이터 */
export type TicketAcquisitionDatum = {
  name: string;
  value: number;
  count: number;
};

const TICKET_COLORS = [
  '#22c55e', // green-500
  '#3b82f6', // blue-500
  '#fb923c', // orange-400 (red 대신)
  '#fbbf24', // yellow-400
  '#94a3b8',
];
const BONUS_CHART_COLORS = ['#22c55e', '#e5e7eb'];

type Props = {
  ticketData?: TicketAcquisitionDatum[];
  bonusPercent?: number;
  bonusCount?: number;
};

export default function RouletteContentTicketAcquisitionCharts({
  ticketData,
  bonusPercent,
  bonusCount = 0,
}: Props) {
  const data = ticketData ?? [];
  const showBonus = bonusPercent !== undefined;
  /** 보너스: 실제 bonusPercent 기준 */
  const bonusChartData = [
    { name: '보너스', value: Math.min(100, Math.max(0, bonusPercent ?? 0)) },
    { name: '기타', value: Math.min(100, Math.max(0, 100 - (bonusPercent ?? 0))) },
  ].filter((d) => d.value > 0);

  return (
    <Box>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        mb={2}
        color="#475569"
        sx={{ fontSize: '0.95rem' }}
      >
        티켓 획득 현황
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          ml: 6,
        }}
      >
        {/* 1~5티켓 도넛 + 범례 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexShrink: 0 }}>
            {data.map((entry, idx) => (
              <Box
                key={entry.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5,
                  fontSize: '0.8125rem',
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    backgroundColor: TICKET_COLORS[idx % TICKET_COLORS.length],
                  }}
                />
                <Typography component="span" variant="body2">
                  {entry.name} ({entry.value}% | {entry.count.toLocaleString('ko-KR')}명)
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={44}
                  outerRadius={70}
                  dataKey="value"
                  stroke="none"
                >
                  {data.map((_, idx) => (
                    <Cell key={idx} fill={TICKET_COLORS[idx % TICKET_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* 보너스 티켓 도넛 (중앙 텍스트) */}
        {showBonus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 140, height: 140, flexShrink: 0, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bonusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    dataKey="value"
                    stroke="none"
                  >
                    {bonusChartData.map((entry, idx) => (
                      <Cell
                        key={entry.name}
                        fill={
                          entry.name === '보너스' ? BONUS_CHART_COLORS[0] : BONUS_CHART_COLORS[1]
                        }
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="body2" fontWeight={600} color="#374151">
                  보너스 티켓
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bonusPercent}% ({bonusCount.toLocaleString('ko-KR')}명)
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
